import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CircleNotch, ArrowLeft } from '@phosphor-icons/react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiErrorDetail, formatExpiryDateTime } from '../lib/utils';
import { emailErrorMessage, validateEmailParts } from '../lib/contactValidation';
import { getBackendOrigin } from '../lib/api';

const LOGO_FULL = '/images/logo-full.png';

function registrationPageErrorMessage(status, data, t) {
  const raw = data?.detail;
  const formatted = formatApiErrorDetail(raw);
  const rawStr = typeof raw === 'string' ? raw : '';
  if (status === 404) {
    if (rawStr.includes("We couldn't issue") || rawStr.includes('organisation')) {
      return formatted;
    }
    const looksLikeRouteMissing =
      formatted === 'Not Found' ||
      /^not found$/i.test(String(formatted).trim()) ||
      formatted === 'Something went wrong. Please try again.';
    if (looksLikeRouteMissing || !raw) {
      return t('auth.mobileReg404Help');
    }
    return formatted || t('auth.mobileReg404Help');
  }
  return formatted || t('auth.mobileRegErrorGeneric');
}

/**
 * Public flow: user enters official email → API returns an enrolment QR (INV) or admin mobile sign-in QR (ULOG).
 * User scans from the Omnichek app (same mobile registration entry as field users).
 */
export default function MobileUserRegistrationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bundle, setBundle] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const emailCheck = validateEmailParts(email);
    if (!emailCheck.valid) {
      setError(emailErrorMessage(emailCheck, t));
      return;
    }
    setLoading(true);
    setBundle(null);
    try {
      const origin = getBackendOrigin();
      if (!origin) {
        setError('Application is not configured to reach the API.');
        return;
      }
      const res = await axios.post(
        `${origin}/api/auth/mobile-registration-qr-by-email`,
        { email: emailCheck.email },
        { headers: { 'Content-Type': 'application/json' }, validateStatus: () => true },
      );
      const data = res.data;
      if (res.status !== 200) {
        setError(registrationPageErrorMessage(res.status, data, t));
        return;
      }
      if (!data?.qr_image_base64 || !data?.qr_payload) {
        setError(t('auth.mobileRegErrorGeneric'));
        return;
      }
      setBundle({
        registration_kind: data.registration_kind,
        invite_code: data.invite_code,
        qr_image_base64: data.qr_image_base64,
        expires_at: data.expires_at,
      });
    } catch (err) {
      const st = err.response?.status;
      const dat = err.response?.data;
      if (st && dat !== undefined) {
        setError(registrationPageErrorMessage(st, dat, t));
      } else {
        setError(t('auth.mobileRegErrorGeneric'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-10">
      <div className="w-full max-w-lg shadow-lg rounded-xl px-6 sm:px-10 py-10 bg-white border border-zinc-200">
        <div className="mb-6 text-center">
          <img src={LOGO_FULL} alt="OMNICHEK" className="h-10 object-contain mx-auto" />
          <h1 className="mt-6 text-xl font-semibold text-zinc-900">{t('auth.mobileRegTitle')}</h1>
        </div>

        {!bundle ? (
          <form onSubmit={submit} className="space-y-5">
            {error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm whitespace-pre-line">
                {error}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="offemail" className="text-zinc-700">
                {t('auth.mobileRegOfficialEmail')}
              </Label>
              <Input
                id="offemail"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('common.emailPlaceholder')}
                required
                className="bg-white border-zinc-300 text-zinc-900 h-11"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full h-11 font-medium"
            >
              {loading ? <CircleNotch className="w-5 h-5 animate-spin" /> : t('auth.mobileRegShowQr')}
            </Button>
          </form>
        ) : (
          <div className="space-y-5">
            <p className="text-center text-sm font-medium text-green-800">{t('auth.mobileRegQrReady')}</p>

            <div className="flex justify-center">
              <img
                alt={t('auth.mobileRegQrAlt')}
                className="w-64 h-64 border border-zinc-200 rounded-lg bg-white"
                src={`data:image/png;base64,${bundle.qr_image_base64}`}
              />
            </div>

            {bundle.expires_at ? (
              <p className="text-center text-xs text-zinc-500">
                {t('auth.mobileRegExpires')} {formatExpiryDateTime(bundle.expires_at)}
                <span className="text-zinc-400"> (IST)</span>
              </p>
            ) : null}

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setBundle(null);
                setEmail('');
              }}
            >
              {t('auth.mobileRegDifferentEmail')}
            </Button>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-zinc-200">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-sm text-brand-600 font-medium hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToSignIn')}
          </button>
          <p className="mt-4 text-center text-sm text-zinc-500">
            {t('auth.noAccount')}{' '}
            <a href="/register/company" className="text-brand-600 font-medium hover:underline">
              {t('auth.createCompany')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
